import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Paperclip, Upload, Trash2, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Reusable attachments panel for any entity (machine / stock / equipment)
export function Attachments({ entityType, entityId }) {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState([]); // [{name, pct}]

  const load = async () => {
    try {
      const res = await api.get("/attachments", { params: { entity_type: entityType, entity_id: entityId } });
      setFiles(res.data);
    } catch (e) {}
  };
  useEffect(() => { if (entityId) load(); /* eslint-disable-next-line */ }, [entityId, entityType]);

  const uploadFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setUploading(true);
    setProgress(arr.map((f) => ({ name: f.name, pct: 0 })));
    let ok = 0, fail = 0;
    for (let i = 0; i < arr.length; i++) {
      const file = arr[i];
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: 10MB üstü, atlandı`); fail++;
        setProgress((p) => p.map((x, idx) => idx === i ? { ...x, pct: -1 } : x));
        continue;
      }
      const fd = new FormData();
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      fd.append("file", file);
      try {
        await api.post("/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setProgress((p) => p.map((x, idx) => idx === i ? { ...x, pct } : x));
          },
        });
        ok++;
        setProgress((p) => p.map((x, idx) => idx === i ? { ...x, pct: 100 } : x));
      } catch (err) {
        fail++;
        setProgress((p) => p.map((x, idx) => idx === i ? { ...x, pct: -1 } : x));
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} dosya yüklendi`);
    if (fail) toast.error(`${fail} dosya yüklenemedi`);
    setTimeout(() => setProgress([]), 1500);
    load();
  };

  const onUpload = async (e) => { await uploadFiles(e.target.files); e.target.value = ""; };
  const onDrop = async (e) => { e.preventDefault(); setDragOver(false); await uploadFiles(e.dataTransfer.files); };

  const remove = async (id) => {
    await api.delete(`/attachments/${id}`);
    toast.success("Dosya silindi");
    load();
  };

  const isImg = (ct) => (ct || "").startsWith("image/");

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5" /> Belge / Fotoğraf ({files.length})
        </p>
        <label>
          {canEdit && <>
          <input data-testid={`upload-input-${entityType}`} type="file" multiple className="hidden" onChange={onUpload} accept="image/*,.pdf,.csv,.txt" />
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer bg-secondary text-white px-2.5 py-1.5 rounded-sm hover:bg-secondary/90">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Yükle
          </span>
          </>}
        </label>
      </div>
      {canEdit && (
      <div
        data-testid={`dropzone-${entityType}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-sm border-2 border-dashed transition-colors duration-200 ${dragOver ? "border-primary bg-primary/5" : "border-border"} ${files.length === 0 ? "" : "mb-2"}`}
      >
        <p className="text-xs text-muted-foreground py-2 text-center">
          {dragOver ? "Bırakın, yüklensin..." : "Fotoğrafları buraya sürükleyip bırakın (birden çok seçebilirsiniz)"}
        </p>
      </div>
      )}
      {progress.length > 0 && (
        <div className="space-y-1.5 mb-2" data-testid="upload-progress">
          {progress.map((p, i) => (
            <div key={i}>
              <div className="flex justify-between text-[11px] mb-0.5">
                <span className="truncate max-w-[70%]">{p.name}</span>
                <span className={p.pct === -1 ? "text-red-600" : "text-muted-foreground"}>{p.pct === -1 ? "Hata" : `%${p.pct}`}</span>
              </div>
              <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
                <div className={`h-full rounded-sm transition-all duration-200 ${p.pct === -1 ? "bg-red-500" : "bg-primary"}`} style={{ width: `${p.pct === -1 ? 100 : p.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
      {files.length === 0 ? (
        <p className="text-xs text-muted-foreground py-3 text-center bg-zinc-50 rounded-sm border border-dashed border-border">Henüz dosya eklenmedi</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {files.map((f) => {
            const url = `${API}/attachments/${f.id}/download`;
            return (
              <div key={f.id} data-testid={`attachment-${f.id}`} className="border border-border rounded-sm p-2 bg-white group relative">
                <a href={url} target="_blank" rel="noreferrer" className="block">
                  {isImg(f.content_type) ? (
                    <img src={url} alt={f.original_filename} className="w-full h-20 object-cover rounded-sm" />
                  ) : (
                    <div className="w-full h-20 flex items-center justify-center bg-zinc-50 rounded-sm">
                      <FileText className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  <p className="text-[11px] truncate mt-1" title={f.original_filename}>{f.original_filename}</p>
                </a>
                {canEdit && (
                <button data-testid={`delete-attachment-${f.id}`} onClick={() => remove(f.id)} className="absolute top-1 right-1 bg-white/90 border border-border rounded-sm p-1 text-red-600 hover:text-red-700 hover:bg-white shadow-sm">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
